import React, { useEffect, useState, useMemo } from "react";
import "../../../css/QavachStaffTable.css";
import api from "../../../api/axiosConfig";
import { showToast } from "../../Shared/Toaster";
import ConfirmationModal from "./ConfirmationModal";
import ViewDoctorApplicationModal, {
  DoctorApplicationData,
} from "./ViewDoctorApplicationModal";

export interface ReferralNomination extends DoctorApplicationData {
  _id: string;
  nominationId?: string;
  parentUserId?: string;
  parentName?: string;
  parentEmail?: string;
  doctorName?: string;
  doctorEmail?: string;
  specialization?: string;
  institutionName?: string;
  address?: string;
  city?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

const ReferralDoctorTable: React.FC = () => {
  const [nominations, setNominations] = useState<ReferralNomination[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // State for View/Review Doctor Application Modal
  const [selectedDoctorForView, setSelectedDoctorForView] =
    useState<ReferralNomination | null>(null);

  // State for Send Invite confirmation modal
  const [selectedNominationForInvite, setSelectedNominationForInvite] =
    useState<ReferralNomination | null>(null);
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  // State for Add Doctor modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [doctorNameInput, setDoctorNameInput] = useState("");
  const [doctorEmailInput, setDoctorEmailInput] = useState("");
  const [isSubmittingDoctor, setIsSubmittingDoctor] = useState(false);

  const fetchNominations = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      // 1. Fetch Doctor Applications (has details & uploaded documents)
      let appsList: ReferralNomination[] = [];
      try {
        const appsRes = await api.Referal_doc.get(
          "/api/admin/doctor-applications",
          { headers }
        );
        appsList = appsRes.data?.applications || appsRes.data?.data || [];
      } catch (err) {
        console.warn("Error fetching doctor applications:", err);
      }

      // 2. Fetch Referral Nominations
      let nomsList: ReferralNomination[] = [];
      try {
        const nomsRes = await api.Referal_doc.get(
          "/api/admin/referral-nominations",
          { headers }
        );
        nomsList = nomsRes.data?.nominations || nomsRes.data?.data || [];
      } catch (err) {
        console.warn("Error fetching referral nominations:", err);
      }

      // Merge data strictly keyed by normalized email
      const emailMap = new Map<string, ReferralNomination>();

      // 1. Add nominations first
      nomsList.forEach((n) => {
        const emailKey = (n.doctorEmail || n.email || "").trim().toLowerCase();
        const key = emailKey || n._id;
        if (key) {
          emailMap.set(key, {
            ...n,
            nominationId: n._id,
            doctorName: n.doctorName || n.name || "—",
            doctorEmail: n.doctorEmail || n.email || "—",
            specialization:
              n.specialization || n.doctorType || n.degreeQualification || "—",
            city: n.city || n.practiceCity || "—",
            institutionName: n.institutionName || "—",
            status: n.status || "PENDING_ADMIN_REVIEW",
          });
        }
      });

      // 2. Override / merge with doctor applications
      appsList.forEach((a) => {
        const emailKey = (a.email || a.doctorEmail || "").trim().toLowerCase();
        const key = emailKey || a._id;
        const existing = emailMap.get(key);

        const doctorName =
          a.name ||
          `${a.firstName || ""} ${a.lastName || ""}`.trim() ||
          a.doctorName ||
          existing?.doctorName ||
          "—";

        emailMap.set(key, {
          ...existing,
          ...a,
          nominationId: existing?.nominationId || existing?._id || a._id,
          _id: a._id || existing?._id || "",
          doctorName: doctorName,
          doctorEmail: a.email || a.doctorEmail || existing?.doctorEmail || "—",
          specialization:
            a.doctorType ||
            a.degreeQualification ||
            a.specialization ||
            existing?.specialization ||
            "—",
          city: a.practiceCity || a.city || existing?.city || "—",
          institutionName:
            a.institutionName || existing?.institutionName || "—",
          status: a.status || a.accountStatus || existing?.status || "DRAFT",
          parentName: existing?.parentName || a.parentName || "—",
          parentEmail: existing?.parentEmail || a.parentEmail || "",
        });
      });

      setNominations(Array.from(emailMap.values()));
    } catch (error) {
      console.error("Error fetching referral nominations/applications:", error);
      setNominations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNominations();
  }, []);

  const filteredNominations = useMemo(() => {
    return nominations
      .filter((doc) => {
        const query = searchTerm.toLowerCase();
        const docName = doc.doctorName || doc.name || "";
        const docEmail = doc.doctorEmail || doc.email || "";

        return (
          docName.toLowerCase().includes(query) ||
          docEmail.toLowerCase().includes(query) ||
          (doc.specialization || "").toLowerCase().includes(query) ||
          (doc.institutionName || "").toLowerCase().includes(query) ||
          (doc.city || "").toLowerCase().includes(query) ||
          (doc.parentName || "").toLowerCase().includes(query)
        );
      })
      .filter((doc) => {
        if (statusFilter === "All") return true;
        return (doc.status || "").toUpperCase() === statusFilter.toUpperCase();
      });
  }, [nominations, searchTerm, statusFilter]);

  const uniqueStatuses = useMemo(() => {
    const set = new Set<string>();
    nominations.forEach((n) => {
      if (n.status) set.add(n.status);
    });
    return ["All Statuses", ...Array.from(set)];
  }, [nominations]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredNominations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNominationsToDisplay = filteredNominations.slice(
    startIndex,
    endIndex
  );

  const formatStatus = (rawStatus?: string) => {
    if (!rawStatus) return "Unknown";
    const statusMap: Record<string, string> = {
      PENDING_ADMIN_REVIEW: "Pending Review",
      UNDER_REVIEW: "Under Review",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      ACTIVE: "Active",
      DISABLED: "Disabled",
      DRAFT: "Draft",
    };
    return (
      statusMap[rawStatus.toUpperCase()] ||
      rawStatus.replace(/_/g, " ").toLowerCase()
    );
  };

  const formatSpecialization = (rawSpec?: string) => {
    if (!rawSpec || rawSpec === "-") return "-";
    if (rawSpec.toUpperCase() === "REFERRAL_DOCTOR") return "Referral Doctor";
    return rawSpec.replace(/_/g, " ");
  };

  const getStatusClass = (rawStatus: string) => {
    const s = (rawStatus || "").toLowerCase();
    if (s.includes("pending") || s.includes("under_review") || s.includes("review"))
      return "onboard-users-status-pending";
    if (s.includes("approved") || s.includes("active"))
      return "onboard-users-status-active";
    if (s.includes("rejected") || s.includes("disabled"))
      return "onboard-users-status-disabled";
    return "onboard-users-status-unassigned";
  };

  const shouldShowSendInvite = (rawStatus?: string) => {
    if (!rawStatus) return false;
    const s = rawStatus.toUpperCase();
    return (
      s === "PENDING_ADMIN_REVIEW" ||
      s === "REJECTED" ||
      s.includes("PENDING") ||
      s.includes("REJECT")
    );
  };

  // Handler for sending invitation after confirmation
  const handleConfirmSendInvite = async () => {
    if (!selectedNominationForInvite) return;
    setIsSendingInvite(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const inviteTargetId =
        selectedNominationForInvite.nominationId ||
        selectedNominationForInvite._id;

      await api.Referal_doc.post(
        `/api/admin/referral-nominations/${inviteTargetId}/send-invite`,
        {},
        { headers }
      );

      showToast("Invitation sent successfully! ✅", "success");
      fetchNominations();
    } catch (err: any) {
      console.error("Error sending invitation:", err);
      showToast(
        err.response?.data?.message || "Failed to send invitation ❌",
        "error"
      );
    } finally {
      setIsSendingInvite(false);
      setSelectedNominationForInvite(null);
    }
  };

  // Handler for submitting Add Doctor nomination
  const handleAddDoctorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doctorNameInput.trim() || !doctorEmailInput.trim()) {
      showToast("Doctor Name and Email are required.", "error");
      return;
    }

    setIsSubmittingDoctor(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const payload = {
        name: doctorNameInput.trim(),
        email: doctorEmailInput.trim(),
      };

      await api.Referal_doc.post(
        "/api/super-admin/doctor-nominations",
        payload,
        { headers }
      );

      showToast("Doctor nomination added successfully! ✅", "success");
      setIsAddModalOpen(false);
      setDoctorNameInput("");
      setDoctorEmailInput("");
      fetchNominations();
    } catch (err: any) {
      console.error("Error adding doctor nomination:", err);
      showToast(
        err.response?.data?.message || "Could not add doctor nomination ❌",
        "error"
      );
    } finally {
      setIsSubmittingDoctor(false);
    }
  };

  return (
    <div className="onboard-users-table-container">
      <div className="onboard-users-table-header">
        <div className="global-filters-container">
          <div className="global-search-wrapper">
            <svg
              className="global-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search by doctor, email, city..."
              className="global-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="global-select-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {uniqueStatuses.map((st) => (
              <option key={st} value={st === "All Statuses" ? "All" : st}>
                {st === "All Statuses" ? st : formatStatus(st)}
              </option>
            ))}
          </select>
        </div>

        <button
          className="add-doctor-btn"
          onClick={() => setIsAddModalOpen(true)}
        >
          + Add Doctor
        </button>
      </div>

      {loading ? (
        <p>Loading referral doctors...</p>
      ) : (
        <div className="table-responsive-wrapper">
          <table className="onboard-users-table referral-table">
            <thead>
              <tr>
                <th style={{ width: "60px", textAlign: "center" }}>No.</th>
                <th>Doctor Name</th>
                <th>Doctor Email</th>
                <th>Specialization</th>
                <th>Institution / Hospital</th>
                <th>City</th>
                <th>Status</th>
                <th>Nominated By</th>
                <th>Created On</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentNominationsToDisplay.length > 0 ? (
                currentNominationsToDisplay.map((doc, index) => {
                  const displayName =
                    doc.doctorName ||
                    doc.name ||
                    `${doc.firstName || ""} ${doc.lastName || ""}`.trim() ||
                    "";
                  const displayEmail = doc.doctorEmail || doc.email || "";

                  return (
                    <tr
                      key={doc._id}
                      onClick={() => setSelectedDoctorForView(doc)}
                      style={{ cursor: "pointer" }}
                      title="Click row to view uploaded details and documents"
                    >
                      <td style={{ textAlign: "center" }}>{startIndex + index + 1}</td>
                      <td>
                        <b>{displayName}</b>
                      </td>
                      <td>{displayEmail}</td>
                      <td>
                        {formatSpecialization(
                          doc.specialization ||
                            doc.doctorType ||
                            doc.degreeQualification
                        )}
                      </td>
                      <td>{doc.institutionName || "-"}</td>
                      <td>{doc.city || doc.practiceCity || "-"}</td>
                      <td>
                        <span
                          className={`onboard-users-badge ${getStatusClass(
                            doc.status || ""
                          )}`}
                        >
                          {formatStatus(doc.status || "")}
                        </span>
                      </td>
                      <td>
                        <div className="referral-nominated-cell">
                          <span className="referral-nominated-name">
                            {doc.parentName || "-"}
                          </span>
                          {doc.parentEmail && (
                            <span className="referral-nominated-email">
                              ({doc.parentEmail})
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {doc.createdAt
                          ? new Date(doc.createdAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {shouldShowSendInvite(doc.status) ? (
                          <button
                            className="btn-send-invite"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedNominationForInvite(doc);
                            }}
                            disabled={isSendingInvite}
                          >
                            Send Invite
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center" }}>
                    No referral doctors found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 0 && (
        <div className="onboard-users-pagination-footer">
          <p>
            Showing {startIndex + 1} to{" "}
            {Math.min(endIndex, filteredNominations.length)} of{" "}
            {filteredNominations.length} results
          </p>
          <div className="onboard-users-pagination">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={currentPage === pageNumber ? "active" : ""}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View/Review Doctor Application Modal */}
      {selectedDoctorForView && (
        <ViewDoctorApplicationModal
          application={selectedDoctorForView}
          onClose={() => setSelectedDoctorForView(null)}
          onSuccess={() => fetchNominations()}
        />
      )}

      {/* Confirmation Modal for Send Invite */}
      {selectedNominationForInvite && (
        <ConfirmationModal
          message={`Are you sure you want to send an invitation to ${
            selectedNominationForInvite.doctorName ||
            selectedNominationForInvite.name ||
            selectedNominationForInvite.doctorEmail ||
            selectedNominationForInvite.email ||
            "this doctor"
          }?`}
          onConfirm={handleConfirmSendInvite}
          onCancel={() => setSelectedNominationForInvite(null)}
        />
      )}

      {/* Add Doctor Popup Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={() => setIsAddModalOpen(false)}>
          <div
            className="modal-container"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "440px",
              padding: "24px",
              background: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            }}
          >
            <div
              className="modal-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "18px",
                borderBottom: "1px solid #e5e7eb",
                paddingBottom: "12px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#174350",
                }}
              >
                Add Doctor
              </h2>
              <button
                type="button"
                className="global-close-btn"
                onClick={() => setIsAddModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleAddDoctorSubmit}>
              <div className="form-group" style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#174350",
                    marginBottom: "6px",
                  }}
                >
                  Doctor Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter doctor name"
                  value={doctorNameInput}
                  onChange={(e) => setDoctorNameInput(e.target.value)}
                  className="global-search-input"
                  style={{
                    width: "100%",
                    height: "42px",
                    boxSizing: "border-box",
                  }}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: "22px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#174350",
                    marginBottom: "6px",
                  }}
                >
                  Doctor Email <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter doctor email"
                  value={doctorEmailInput}
                  onChange={(e) => setDoctorEmailInput(e.target.value)}
                  className="global-search-input"
                  style={{
                    width: "100%",
                    height: "42px",
                    boxSizing: "border-box",
                  }}
                  required
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  style={{
                    padding: "10px 18px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    background: "#f3f4f6",
                    color: "#374151",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingDoctor}
                  style={{
                    padding: "10px 22px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#174350",
                    color: "#ffffff",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "14px",
                    opacity: isSubmittingDoctor ? 0.7 : 1,
                  }}
                >
                  {isSubmittingDoctor ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralDoctorTable;
